import React from 'react'
import { Box, Tooltip, Typography, makeStyles } from '@material-ui/core'
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty'
import NotificationImportantIcon from '@material-ui/icons/NotificationImportant'

export type NextActionOwner = 'us' | 'them'

export interface NextActionValue {
    waiting_on: NextActionOwner | 'you' | null
    label: string | null
    waiting_since?: string | null
}

export const NEXT_ACTION_US_COLOR = '#d97706'
export const NEXT_ACTION_THEM_COLOR = '#64748b'

const useStyles = makeStyles({
    line: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 8,
        marginTop: 10,
        minWidth: 0,
        width: '100%',
        textAlign: 'left',
    },
    lineText: {
        fontSize: '0.875rem',
        fontWeight: 700,
        lineHeight: 1.35,
        minWidth: 0,
    },
    tableIcon: {
        fontSize: '1.125rem',
        flexShrink: 0,
        display: 'block',
    },
    lineIcon: {
        fontSize: '1.35rem',
        flexShrink: 0,
        display: 'block',
    },
})

export function formatWaitingDuration(iso?: string | null): string | null {
    if (!iso) {
        return null
    }
    const then = new Date(iso).getTime()
    if (Number.isNaN(then)) {
        return null
    }
    const diffMs = Date.now() - then
    if (diffMs < 0) {
        return 'just now'
    }
    const seconds = Math.floor(diffMs / 1000)
    if (seconds < 60) {
        return 'just now'
    }
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) {
        return minutes === 1 ? '1 minute' : `${minutes} minutes`
    }
    const hours = Math.floor(minutes / 60)
    if (hours < 24) {
        return hours === 1 ? '1 hour' : `${hours} hours`
    }
    const days = Math.floor(hours / 24)
    if (days < 60) {
        return days === 1 ? '1 day' : `${days} days`
    }
    try {
        return new Date(iso).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
        })
    } catch {
        return null
    }
}

export function resolveNextActionOwner(
    waitingOn?: NextActionValue['waiting_on'],
): NextActionOwner | null {
    if (waitingOn === 'us') {
        return 'us'
    }
    if (waitingOn === 'them' || waitingOn === 'you') {
        return 'them'
    }
    return null
}

export function nextActionSentence(
    label: string,
    waitingSince?: string | null,
): string {
    const age = formatWaitingDuration(waitingSince)
    return age ? `Next action · ${label} · ${age}` : `Next action · ${label}`
}

export function NextActionIcon({
    owner,
    className,
    fontSize,
}: {
    owner: NextActionOwner
    className?: string
    fontSize?: number
}) {
    const color = owner === 'us' ? NEXT_ACTION_US_COLOR : NEXT_ACTION_THEM_COLOR
    const Icon = owner === 'us' ? NotificationImportantIcon : HourglassEmptyIcon
    return (
        <Icon
            className={className}
            htmlColor={color}
            style={fontSize ? { fontSize } : undefined}
        />
    )
}

export function NextActionTableIcon({
    action,
}: {
    action?: NextActionValue | null
}) {
    const classes = useStyles()
    const owner = resolveNextActionOwner(action?.waiting_on)
    if (!owner || !action?.label) {
        return null
    }
    return (
        <Tooltip title={nextActionSentence(action.label, action.waiting_since)}>
            <span>
                <NextActionIcon owner={owner} className={classes.tableIcon} />
            </span>
        </Tooltip>
    )
}

export function NextActionLine({
    action,
}: {
    action?: NextActionValue | null
}) {
    const classes = useStyles()
    const owner = resolveNextActionOwner(action?.waiting_on)
    if (!owner || !action?.label) {
        return null
    }
    const color = owner === 'us' ? NEXT_ACTION_US_COLOR : NEXT_ACTION_THEM_COLOR
    return (
        <Box className={classes.line}>
            <NextActionIcon owner={owner} className={classes.lineIcon} />
            <Typography className={classes.lineText} style={{ color }}>
                {nextActionSentence(action.label, action.waiting_since)}
            </Typography>
        </Box>
    )
}
